import "./styles/globals.js";

import { initI18n } from "./i18n/index.js";
import { bootSession, currentSession } from "./state/session.js";
import { defineRoutes } from "./router/index.js";
import { attachUserSocket, refresh as refreshNotifications } from "./state/notifications.js";
import { enforceLegalAcceptance } from "./ui/legal-gate.js";
import { initCadenceTracker } from "./captcha/fingerprint.js";
import { startMaintenanceWatcher } from "./ui/maintenance-overlay.js";
import { startPresenceReporter } from "./ui/presence.js";
import { initTooltipPortal } from "./ui/tooltip.js";
import { registerServiceWorker } from "./ui/pwa.js";
import { initConsentBanner } from "./ui/consent-banner.js";
import { initAntiDevtools } from "./ui/anti-devtools.js";

async function boot(): Promise<void> {
  initCadenceTracker();

  initTooltipPortal();

  registerServiceWorker();

  await consumeBypassFromUrl();

  const session = await bootSession();
  initI18n(session.config.defaultLocale);

  startMaintenanceWatcher(session.config.maintenance);
  startPresenceReporter();

  const savedTheme = localStorage.getItem("dropl:theme");
  if (savedTheme === "dark" || savedTheme === "light") {
    document.documentElement.dataset.theme = savedTheme;
  }

  document.title = session.config.name;

  initConsentBanner();

  initAntiDevtools();

  if (currentSession().user) {
    attachUserSocket();
    void refreshNotifications();
    void enforceLegalAcceptance();
  }

  const isAuthed = (): boolean => currentSession().user !== null;
  const isAdmin  = (): boolean => {
    const u = currentSession().user;
    return u !== null && u.capabilities.permissions.includes("admin.access");
  };

  const isMaintenanceWhitelisted = (path: string): boolean => {
    const clean = path.split("?")[0] ?? path;
    if (clean === "/maintenance") return true;
    if (clean === "/login") return true;
    if (clean === "/forgot-password") return true;
    if (clean.startsWith("/auth/")) return true;
    if (clean.startsWith("/legal")) return true;
    if (clean.startsWith("/admin")) return true;
    return false;
  };

  const maintenanceGuard = (ctx: { path: string }): string | null => {
    const m = currentSession().config.maintenance;
    if (!m.enabled) return null;

    if (m.bypassed) return null;
    const u = currentSession().user;
    const perms = u?.capabilities.permissions ?? [];
    const canBypass =
      perms.includes("maintenance.bypass") ||
      (m.allowAdmin && perms.includes("admin.access"));
    if (canBypass) return null;
    if (isMaintenanceWhitelisted(ctx.path)) return null;
    return "/maintenance";
  };

  defineRoutes(
    [
      { path: "/", load: () => import("./pages/landing.js") },
      { path: "/maintenance", load: () => import("./pages/maintenance.js") },
      { path: "/d/:slug", load: () => import("./pages/download.js") },
      { path: "/drop/:slug", load: () => import("./pages/drop.js") },
      { path: "/legal",       load: () => import("./pages/legal-index.js") },
      { path: "/legal/:slug", load: () => import("./pages/legal.js") },
      { path: "/login",    load: () => import("./pages/auth/login.js"),
        guard: () => (isAuthed() ? "/app" : null) },
      { path: "/register", load: () => import("./pages/auth/register.js"),
        guard: () => (isAuthed() ? "/app" : null) },
      { path: "/forgot-password", load: () => import("./pages/auth/forgot-password.js"),
        guard: () => (isAuthed() ? "/app" : null) },
      { path: "/auth/reset-password", load: () => import("./pages/auth/reset-password.js") },
      { path: "/auth/verify-email",   load: () => import("./pages/auth/verify-email.js") },
      { path: "/auth/2fa",            load: () => import("./pages/auth/two-factor.js"),
        guard: () => (isAuthed() ? "/app" : null) },
      { path: "/app", load: () => import("./pages/app.js"),
        guard: () => (isAuthed() ? null : "/login") },
      { path: "/app/api", load: () => import("./pages/app-api.js"),
        guard: () => (isAuthed() ? null : "/login") },
      { path: "/app/account", load: () => import("./pages/app-account.js"),
        guard: () => (isAuthed() ? null : "/login") },
      { path: "/admin",         load: () => import("./pages/admin/overview.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/presence", load: () => import("./pages/admin/presence.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/users",   load: () => import("./pages/admin/users.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/roles",      load: () => import("./pages/admin/roles.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/files",      load: () => import("./pages/admin/files.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/storage",    load: () => import("./pages/admin/storage.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/migrations", load: () => import("./pages/admin/migrations.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/notifications", load: () => import("./pages/admin/notifications.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/legal", load: () => import("./pages/admin/legal.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/bot", load: () => import("./pages/admin/bot.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/abuse", load: () => import("./pages/admin/abuse.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/reports", load: () => import("./pages/admin/reports.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/support", load: () => import("./pages/admin/support.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/email-blocklist", load: () => import("./pages/admin/email-blocklist.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/logs", load: () => import("./pages/admin/logs.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/maintenance", load: () => import("./pages/admin/maintenance.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "/admin/settings",   load: () => import("./pages/admin/settings.js"),
        guard: () => (isAdmin() ? null : (isAuthed() ? "/app" : "/login")) },
      { path: "*", load: () => import("./pages/not-found.js") },
    ],
    { mount: "#app", guards: [maintenanceGuard] },
  );
}

async function consumeBypassFromUrl(): Promise<void> {
  try {
    const url = new URL(location.href);
    const bypass = url.searchParams.get("bypass");
    if (!bypass) return;

    await fetch("/api/config?bypass=" + encodeURIComponent(bypass), {
      credentials: "include",
    }).catch(() => null);
    url.searchParams.delete("bypass");
    const clean = url.pathname + (url.search || "") + (url.hash || "");
    history.replaceState({}, "", clean);
  } catch {

  }
}

boot().catch((err: unknown) => {
  const root = document.getElementById("app");
  const msg = err instanceof Error ? err.message : String(err);
  if (root) root.textContent = "Erreur : " + msg;
});
